import { useEffect, useState } from "react";
import { useParams } from "react-router";
import ResponsiveTable, { Column } from "@/components/Usados/ResponsiveTable";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/Usados/modal";
import Button from "@/components/ui/button/Button";
import { Eye } from "lucide-react";
import { apiFetch } from "@/service/api";

interface HistorialCambio {
  id: number;
  fecha_cambio: string;
  campo_modificado: string;
  valor_anterior: string;
  valor_nuevo: string;
  usuario: { id: number; nombres: string; apellidos: string };
  actividad?: { id: number; nombre: string };
}

export default function HistorialCambios() {
  const { id: proyecto_id } = useParams();
  const [historial, setHistorial] = useState<HistorialCambio[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChange, setSelectedChange] = useState<HistorialCambio | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchHistorial = async () => {
      try {
        const response = await apiFetch(
          `historial-cambios-actividad/find-all-proyecto/${proyecto_id}`,
        );
        if (response.ok) {
          const data = await response.json();
          setHistorial(data);
        }
      } catch (error) {
        console.error("Error fetching historial:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistorial();
  }, [proyecto_id]);

  const formatField = (field: string) => {
    const fields: { [key: string]: string } = {
      nombre: "Nombre",
      descripcion: "Descripcion",
      estado_actividad: "Estado",
      prioridad: "Prioridad",
      fecha_inicio: "Fecha Inicio",
      fecha_fin_estimada: "Fecha Fin Estimada",
    };
    return fields[field] || field;
  };

  const handleViewDetails = (change: HistorialCambio) => {
    setSelectedChange(change);
    setIsModalOpen(true);
  };

  const columns: Column<HistorialCambio>[] = [
    {
      label: "Fecha",
      key: "fecha_cambio",
      render: (value: any) => new Date(value).toLocaleString(),
    },
    {
      label: "Actividad",
      key: "actividad",
      render: (actividad: any) => actividad?.nombre || "General",
    },
    {
      label: "Usuario",
      key: "usuario",
      render: (usuario: any) =>
        `${usuario?.nombres || ""} ${usuario?.apellidos || ""}`,
    },
    {
      label: "Campo",
      key: "campo_modificado",
      render: (value: any) => (
        <Badge variant="light" color="primary">
          {formatField(value)}
        </Badge>
      ),
    },
    {
      label: "Acciones",
      key: "actions",
      render: (_, row) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleViewDetails(row)}
          className="flex items-center gap-2"
        >
          <Eye className="size-4" />
          Ver
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <h3 className="mb-6 font-semibold text-gray-800 text-center text-theme-xl dark:text-white/90 sm:text-2xl">
          Registro de Auditoria de Actividades
        </h3>
        {loading ? (
          <div className="flex items-center justify-center p-10">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-brand-500 rounded-full animate-spin"></div>
          </div>
        ) : historial.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500 dark:text-gray-400">
              No hay registros de cambios para este proyecto.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <ResponsiveTable columns={columns} data={historial} />
          </div>
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="max-w- p-6"
      >
        <div className="space-y-6">
          <div className="border-b pb-4">
            <h4 className="text-xl font-semibold text-gray-800 dark:text-white">
              Detalle del Cambio
            </h4>
          </div>
          {selectedChange && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                    Fecha:
                  </p>
                  <p className="text-gray-800 dark:text-white">
                    {new Date(selectedChange.fecha_cambio).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                    Usuario:
                  </p>
                  <p className="text-gray-800 dark:text-white">{`${selectedChange.usuario.nombres} ${selectedChange.usuario.apellidos}`}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                    Actividad:
                  </p>
                  <p className="text-gray-800 dark:text-white">
                    {selectedChange.actividad?.nombre || "General"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                    Campo:
                  </p>
                  <Badge variant="light" color="primary">
                    {formatField(selectedChange.campo_modificado)}
                  </Badge>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                  Valor Anterior:
                </p>
                <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 rounded-lg">
                  <p className="text-red-700 dark:text-red-400 break-words line-through italic">
                    {selectedChange.valor_anterior || "(Vacio)"}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                  Valor Nuevo:
                </p>
                <div className="p-3 bg-green-50 dark:bg-green-500/10 border border-green-100 dark:border-green-500/20 rounded-lg">
                  <p className="text-green-700 dark:text-green-400 break-words font-medium">
                    {selectedChange.valor_nuevo || "(Vacio)"}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setIsModalOpen(false)}>Cerrar</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
